import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import type { FieldArrayWithId } from "react-hook-form";
import { cloneDeep, isEqual, isNull } from "lodash";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Button from "../../../components/Button";
import colors from "../../../theme/colors";
import Modal from "../../../components/Modal";
import type { RootState } from "../../../store";
import {
  completeMetadata,
  MetadataState,
  resetCompleteMetadata,
  saveMetadata,
} from "../slice";
import { cellIdToString } from "../../../utils/labels";
import { getDirtyValues } from "../../../utils/forms";
import TeardownField from "./TeardownField";
import parseCellIdString from "../../../utils/parseCellIdString";

type Props = {
  open: boolean;
  onClose: () => void;
  formField: FieldArrayWithId<MetadataRHFormFormat, "metadata">;
  teardownType?: string;
  teardownFields?: ConditionUIField[];
  teardownState?: Record<string, any>;
  executor: User | null;
};

const TeardownEditModal = ({
  open,
  onClose,
  formField,
  teardownType,
  teardownFields = [],
  teardownState = {},
  executor,
}: Props) => {
  const { cell_id_string = "" } = useParams();

  const defaultValues = useMemo(() => teardownState, [teardownState]);
  const formMethods = useForm({ mode: "onBlur", defaultValues });
  const { getValues, reset, register, trigger, formState } = formMethods;
  const { isDirty } = formState;

  const enabledFields = useMemo(() => {
    const enabledFieldsCopy: { [key: string]: [string] } = {};

    teardownFields
      .filter((field) => "enabled_by" in field)
      .forEach((field) => {
        let enabled_by = field?.enabled_by?.id;
        if (enabled_by) {
          let field_id = field.id;
          if (field.type.includes("*")) {
            let reps = parseInt(field.type.split("*")[1]);
            for (let i = 1; i <= reps; i++) {
              let field_id_reps = `${field_id}__rep_${i}`;
              if (
                enabled_by in enabledFieldsCopy &&
                !(field_id_reps in enabledFieldsCopy[enabled_by])
              ) {
                enabledFieldsCopy[enabled_by].push(field_id_reps);
              } else {
                enabledFieldsCopy[enabled_by] = [field_id_reps];
              }
            }
          } else {
            // Add the conditional value if applicable to the enabled fields
            let conditional_val = field?.enabled_by?.conditional_val;
            if (!!conditional_val) {
              enabled_by = `${enabled_by}==${conditional_val}`;
            }
            let conditional_vals = field?.enabled_by?.conditional_vals;
            if (!!conditional_vals) {
              enabled_by = `${enabled_by}==${conditional_vals}`; // need to figure this out
            }

            if (
              enabled_by in enabledFieldsCopy &&
              !(field_id in enabledFieldsCopy[enabled_by])
            ) {
              enabledFieldsCopy[enabled_by].push(field_id);
            } else {
              enabledFieldsCopy[enabled_by] = [field_id];
            }
          }
        }
      });

    return enabledFieldsCopy;
  }, [teardownFields]);

  // For each field with an enabled_by dependency, walk up the chain to collect
  // all ancestor dependencies. This lets doubly-nested (and deeper) fields hide
  // when any ancestor in the chain is disabled, not just the immediate parent.
  const fieldAncestors = useMemo(() => {
    const enabledByMap = new Map<string, ConditionUIField["enabled_by"]>();
    teardownFields.forEach((field) => {
      if (field.enabled_by) {
        enabledByMap.set(field.id, field.enabled_by);
      }
    });

    const ancestors: Record<
      string,
      NonNullable<ConditionUIField["enabled_by"]>[]
    > = {};
    teardownFields.forEach((field) => {
      if (!field.enabled_by) return;
      const chain: NonNullable<ConditionUIField["enabled_by"]>[] = [];
      let current: ConditionUIField["enabled_by"] | undefined =
        field.enabled_by;
      const visited = new Set<string>();
      while (current && !visited.has(current.id)) {
        chain.push(current);
        visited.add(current.id);
        current = enabledByMap.get(current.id);
      }
      ancestors[field.id] = chain;
    });

    return ancestors;
  }, [teardownFields]);

  const lastResetKeyRef = useRef<string>("");

  const user = useSelector<RootState, null | User>(({ auth }) => auth.user);
  useEffect(() => {
    if (!open) return;

    const teardownId = (teardownState as any)?.id ?? "";
    const resetKey = `${cell_id_string}::${teardownId}`;

    // Only reset when opening the modal (or switching to a different teardown payload)
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;

      const nextDefaults: Record<string, any> = { ...(teardownState || {}) };

      // Pre-fill teardown operators if applicable
      if (
        "teardown_operators" in nextDefaults &&
        isNull(nextDefaults["teardown_operators"])
      ) {
        nextDefaults["teardown_operators"] = user
          ? [user]
          : nextDefaults["teardown_operators"];
      }

      reset(nextDefaults);
    }
  }, [cell_id_string, teardownState, reset, open, user]);

  // Metadata state in redux
  const dispatch = useDispatch();
  const {
    status: { save: saveStatus, complete: completeStatus },
    error: { save: saveError, complete: completeError },
  } = useSelector<RootState, MetadataState>(({ metadata }) => metadata);

  useEffect(() => {
    if (open) {
      dispatch(resetCompleteMetadata());
    }
  }, [dispatch, open]);

  const removeObsoleteData = useCallback((formValues: any) => {
    /**
    Removes form data if the enabled-by field doesn't meet the criteria
    for requiring the dependent field. Transitively cascades: if a deleted
    field itself enables other fields, those are deleted too.
    **/
    let fieldsToDelete: { [key: string]: null } = {};

    // Collect data on conditional values in the format
    // {"key": "value_that_disables_requirement"}
    let conditionalVals: { [key: string]: string } = {};
    Object.keys(enabledFields).forEach((field) => {
      let split_obj = field.split("==");
      if (split_obj.length > 1) {
        conditionalVals[split_obj[0]] = split_obj[1];
      }
    });
    for (const [key, val] of Object.entries(formValues)) {
      if (key in conditionalVals) {
        // If this key has a conditional value for requiring the dependent field,
        // delete the dependent field's data if the value meets the requirement
        let _key = `${key}==${conditionalVals[key]}`;

        if (_key in enabledFields && conditionalVals[key] !== val) {
          enabledFields[_key].forEach((field) => {
            fieldsToDelete[field] = null;
          });
        }
      } else if (key in enabledFields && !!val === false) {
        // Otherwise, just check that the value isn't already null
        enabledFields[key].forEach((field) => {
          fieldsToDelete[field] = null;
        });
      }
    }

    // Transitively cascade: if a deleted field also enables other fields,
    // delete those too
    let changed = true;
    while (changed) {
      changed = false;
      for (const deletedField of Object.keys(fieldsToDelete)) {
        for (const [enabledByKey, children] of Object.entries(enabledFields)) {
          if (
            enabledByKey === deletedField ||
            enabledByKey.startsWith(`${deletedField}==`)
          ) {
            for (const child of children) {
              if (!(child in fieldsToDelete)) {
                fieldsToDelete[child] = null;
                changed = true;
              }
            }
          }
        }
      }
    }

    return {
      ...formValues,
      ...fieldsToDelete,
    };
  }, [enabledFields]);

  const handleSave = useCallback(async () => {
    const values = getValues();

    if (values.completed?.completed_at) {
      const result = await trigger();
      if (!result) {
        return;
      }
    }

    const dirtyValues = removeObsoleteData(
      getDirtyValues(formState.dirtyFields, values)
    );
    const { conditionIdsToRequestedCellIds } =
      parseCellIdString(cell_id_string);

    // Snapshot the form values before dispatching so we can detect
    // concurrent edits made while the save was in-flight.
    const preDispatchValues = cloneDeep(values);

    const action = await dispatch(
      saveMetadata({
        metadata: [{ items: [dirtyValues] }],
        conditionIdsToRequestedCellIds,
      })
    );

    // Only clear isDirty when the save had no errors AND the user did not
    // edit the form while the request was in-flight.
    const payload = (action as any)?.payload;
    const hasErrors =
      payload?.errors && payload.errors.length > 0;
    const postDispatchValues = getValues();
    const cleanedPostDispatchValues = removeObsoleteData(postDispatchValues);
    const hasNewEdits = !isEqual(preDispatchValues, postDispatchValues);

    if (!hasErrors && !hasNewEdits) {
      reset(cleanedPostDispatchValues);
    }
  }, [
    getValues,
    trigger,
    cell_id_string,
    formState,
    dispatch,
    reset,
    removeObsoleteData,
  ]);

  const handleComplete = useCallback(async () => {
    const result = await trigger();
    if (!result) {
      return;
    }

    const { completed } = getValues();
    const { conditionIdsToRequestedCellIds } =
      parseCellIdString(cell_id_string);

    await dispatch(
      completeMetadata({
        components: [{ completed }],
        executor,
        conditionIdsToRequestedCellIds,
      })
    );

    onClose();
  }, [cell_id_string, onClose, dispatch, executor, getValues, trigger]);

  return (
    <Modal open={open} onClose={onClose}>
      <Typography variant="h2">Teardown</Typography>

      <Box mt={6} mb={2} display="flex" alignItems="center">
        <Typography color="textSecondary" fontWeight={500} className="small">
          Cell ID:
        </Typography>
        <Box ml={2} mr={30}>
          <Typography color="textPrimary">
            {cellIdToString(formField.cell_id)}
          </Typography>
        </Box>
        <Box mr={2}>
          <Typography color="textSecondary" fontWeight={500} className="small">
            Type:
          </Typography>
        </Box>
        <Typography color="textPrimary">{teardownType}</Typography>
      </Box>

      {open ? (
        <FormProvider {...formMethods}>
          <form>
            <div style={{ overflow: "auto", height: "50vh" }}>
              {teardownFields.map((field, index) => (
                <TeardownField
                  key={index}
                  field={field}
                  value={teardownState[field.id]}
                  ancestors={fieldAncestors[field.id]}
                />
              ))}
            </div>

            <input
              key={`hidden-${teardownState.id}-api_path`}
              {...register("api_path")}
              type="hidden"
              defaultValue={teardownState.api_path}
            />
            {teardownState.completed?.api_path.do ? (
              <input
                key={`hidden-${teardownState.id}-complete-api_path`}
                {...register("completed.api_path.do")}
                type="hidden"
                defaultValue={teardownState.completed.api_path.do}
              />
            ) : null}
            {teardownState.completed?.api_path.undo ? (
              <input
                key={`hidden-${teardownState.id}-uncomplete-api_path`}
                {...register("completed.api_path.undo")}
                type="hidden"
                defaultValue={teardownState.completed.api_path.undo}
              />
            ) : null}
            <input
              key={`hidden-${teardownState.id}-complete-time`}
              {...register("completed.completed_at")}
              type="hidden"
              defaultValue={teardownState.completed?.completed_at}
            />

            <Box mt={6} display="flex" alignItems="center">
              <div className="small" style={{ color: colors.accent.red }}>
                {saveError}
                {completeError}
              </div>
              <Box ml="auto" display="flex">
                <Button
                  size="small"
                  color="primary"
                  type="button"
                  onClick={() => handleSave()}
                  endIcon={
                    saveStatus === "loading" ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null
                  }
                  disabled={saveStatus === "loading"}
                >
                  Save
                </Button>
                {!teardownState.completed?.completed_at ? (
                  <Box ml={4}>
                    <Button
                      size="small"
                      color="cta"
                      type="button"
                      onClick={() => handleComplete()}
                      endIcon={
                        completeStatus === "loading" ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null
                      }
                      disabled={isDirty || completeStatus === "loading"}
                    >
                      Complete
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </form>
        </FormProvider>
      ) : null}
    </Modal>
  );
};

export default TeardownEditModal;
